import { useMemo, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { allStateCodes, stateInfo } from "@/constants/version-state";
import { useVersionStates } from "@/hooks/version-states";
import type { App } from "@/types/domain";
import { refreshApps } from "./refresh-apps";

// Per-app notify switches: which group-notification events this app posts,
// plus (for version state changes) which target states count. The event
// catalog comes from the server (`/apps/notify/events`) so adding an event
// type doesn't need a matching client edit. Unconfigured = everything on:
// existing apps keep their behaviour, and a newly added event type reaches
// everyone rather than being silently muted by stale config.
const normCodes = (codes: number[]) =>
  [...new Set(codes)].sort((a, b) => a - b).join(",");
const mutedKeys = (prefs: Record<string, boolean>) =>
  Object.entries(prefs)
    .filter(([, on]) => !on)
    .map(([k]) => k)
    .sort()
    .join(",");

interface NotifyEventMeta {
  key: string;
  group: string;
  label: string;
  hasStates?: boolean;
}

export const NotifyEventPrefs = ({ app }: { app: App }) => {
  const { toast } = useToast();
  const { data: events } = useSWR<NotifyEventMeta[]>(["/apps/notify/events"]);
  // DB-managed state catalog (re-renders when it loads).
  useVersionStates();
  const stateCodes = allStateCodes();

  // Server stores only the muted keys; everything absent is on.
  const savedPrefs = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const e of events ?? []) out[e.key] = app.notifyPrefs?.[e.key] !== false;
    return out;
  }, [events, app.notifyPrefs]);
  const savedStates = app.versionNotifyStates ?? stateCodes;

  const [prefs, setPrefs] = useState<Record<string, boolean>>(savedPrefs);
  const [states, setStates] = useState<number[]>(savedStates);
  // Catalog arrives after first render — adopt it once, without clobbering
  // edits the user already made.
  const [touched, setTouched] = useState(false);
  const effectivePrefs = touched ? prefs : savedPrefs;

  const prefsDirty = mutedKeys(effectivePrefs) !== mutedKeys(savedPrefs);
  const statesDirty = normCodes(states) !== normCodes(savedStates);
  const dirty = prefsDirty || statesDirty;

  const { trigger: savePrefs, isMutating: savingPrefs } = useSWRMutation(
    `/apps/${app.id}/notify/prefs`,
    putFetcher,
    { onError: (err: unknown) => toastError(toast, "Save failed", err) }
  );
  const { trigger: saveStates, isMutating: savingStates } = useSWRMutation(
    `/apps/${app.id}/notify/version-states`,
    putFetcher,
    { onError: (err: unknown) => toastError(toast, "Save failed", err) }
  );
  const saving = savingPrefs || savingStates;

  const setPref = (key: string, on: boolean) => {
    setTouched(true);
    setPrefs({ ...effectivePrefs, [key]: on });
  };
  const toggleState = (code: number, on: boolean) =>
    setStates((prev) =>
      on ? [...prev, code] : prev.filter((c) => c !== code)
    );

  const onSave = async () => {
    // All-on saves as null so the app keeps following the default.
    if (prefsDirty) await savePrefs({ prefs: effectivePrefs });
    if (statesDirty) {
      await saveStates({
        states: states.length === stateCodes.length ? null : states,
      });
    }
    refreshApps();
    toast({ title: "Notification settings saved" });
  };

  // Preserve catalog order; the server groups them meaningfully.
  const groups = useMemo(() => {
    const out: { name: string; items: NotifyEventMeta[] }[] = [];
    for (const e of events ?? []) {
      const last = out[out.length - 1];
      if (last?.name === e.group) last.items.push(e);
      else out.push({ name: e.group, items: [e] });
    }
    return out;
  }, [events]);

  if (!events?.length) return null;

  return (
    <div className="flex flex-col gap-3 border-t pt-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Events</span>
        <Button
          size="sm"
          className="h-8"
          disabled={!dirty || saving}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      {groups.map((g) => (
        <div key={g.name} className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground">
            {g.name}
          </span>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            {g.items.map((e) => (
              <label key={e.key} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={effectivePrefs[e.key] !== false}
                  onCheckedChange={(v) => setPref(e.key, v === true)}
                />
                <span>{e.label}</span>
              </label>
            ))}
          </div>
          {/* Second level for state changes: which target states count.
              Judged by the state the version moves *into*. */}
          {g.items.some((e) => e.hasStates && effectivePrefs[e.key] !== false) && (
            <div className="ml-6 grid grid-cols-3 gap-x-4 gap-y-2 border-l pl-4 sm:grid-cols-4">
              {stateCodes.map((code) => (
                <label key={code} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={states.includes(code)}
                    onCheckedChange={(v) => toggleState(code, v === true)}
                  />
                  <span className="text-muted-foreground">
                    {stateInfo(code)?.label ?? code}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      <p className="text-xs text-muted-foreground">
        Unticked events stay out of this app&rsquo;s group. Other apps are
        unaffected, and apps sharing one group still get a single message per
        event. Muting never blocks the underlying action, and 1:1 messages to
        the people involved are not affected.
      </p>
    </div>
  );
};
